import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Plus, Search, RefreshCw, Calendar, Play, Pause, XCircle, Clock, 
  FileText, Repeat, CheckCircle, AlertCircle, ChevronRight, ChevronLeft,
  User, Package, Settings, Zap, ArrowRight, Edit2, Trash2, Eye,
  Mail, MessageSquare, TrendingUp, CalendarDays, ChevronDown, ChevronUp,
  DollarSign
} from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

const STATUS_CONFIG = {
  ACTIVE: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Active', icon: Play },
  PAUSED: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Paused', icon: Pause },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed', icon: CheckCircle },
  CANCELLED: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', icon: XCircle }
};

const FREQUENCIES = [
  { value: 'DAILY', label: 'Daily', description: 'Every day' },
  { value: 'WEEKLY', label: 'Weekly', description: 'Every 7 days' },
  { value: 'BIWEEKLY', label: 'Bi-Weekly', description: 'Every 2 weeks' },
  { value: 'MONTHLY', label: 'Monthly', description: 'Every month' },
  { value: 'QUARTERLY', label: 'Quarterly', description: 'Every 3 months' },
  { value: 'HALF_YEARLY', label: 'Half-Yearly', description: 'Every 6 months' },
  { value: 'YEARLY', label: 'Yearly', description: 'Once a year' }
];

const ProfileRow = ({ profile, index, onAction, bid, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[profile.status] || STATUS_CONFIG.ACTIVE;
  const StatusIcon = statusConfig.icon;

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  return (
    <>
      <tr 
        className={`tl-tr hover:bg-violet-50/40 cursor-pointer ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="tl-td" style={{ width: '3%' }}>
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </td>
        <td className="tl-td" style={{ width: '18%' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Repeat className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <p className="tl-cell-primary text-[12px] font-medium text-slate-800">{profile.profile_name}</p>
              <p className="text-[10px] text-slate-400">{profile.customer_name}</p>
            </div>
          </div>
        </td>
        <td className="tl-td" style={{ width: '10%' }}>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
            <Repeat className="w-3 h-3" /> {profile.frequency_label || profile.frequency}
          </span>
        </td>
        <td className="tl-td" style={{ width: '12%' }}>
          <p className="tl-cell-regular text-[12px]">
            {profile.next_invoice_date ? new Date(profile.next_invoice_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
          </p>
          <p className="text-[10px] text-slate-400">Next Invoice</p>
        </td>
        <td className="tl-td tl-td-right" style={{ width: '12%' }}>
          <p className="font-semibold text-violet-600 text-[13px]">{formatCurrency(profile.total_amount)}</p>
        </td>
        <td className="tl-td" style={{ width: '10%' }}>
          <p className="text-[12px] text-slate-600">{profile.generated_count || 0} invoices</p>
        </td>
        <td className="tl-td" style={{ width: '10%' }}>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${statusConfig.bg} ${statusConfig.text}`}>
            <StatusIcon className="w-3 h-3" /> {statusConfig.label}
          </span>
        </td>
        <td className="tl-td" style={{ width: '15%' }} onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            {profile.status === 'ACTIVE' && (
              <>
                <button onClick={() => onAction(profile.id, 'generate')} className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-md" title="Generate Now">
                  <Zap className="w-4 h-4" />
                </button>
                <button onClick={() => onAction(profile.id, 'pause')} className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-md" title="Pause">
                  <Pause className="w-4 h-4" />
                </button>
              </>
            )}
            {profile.status === 'PAUSED' && (
              <button onClick={() => onAction(profile.id, 'resume')} className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md" title="Resume">
                <Play className="w-4 h-4" />
              </button>
            )}
            {['ACTIVE', 'PAUSED'].includes(profile.status) && (
              <button onClick={() => { if (window.confirm('Stop permanently?')) onAction(profile.id, 'cancel'); }} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md" title="Stop">
                <XCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </td>
      </tr>
      
      {/* Expanded Row */}
      {expanded && (
        <tr className="bg-gradient-to-r from-violet-50/50 to-slate-50/50">
          <td colSpan={8} className="px-6 py-4">
            <div className="grid grid-cols-3 gap-5">
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-violet-600 uppercase font-bold tracking-wider mb-3">Schedule Details</p>
                <div className="space-y-2 text-[12px]">
                  <div className="flex justify-between"><span className="text-slate-500">Frequency:</span><span className="text-slate-800 font-medium">{profile.frequency}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Start Date:</span><span className="text-slate-800">{profile.start_date || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">End Date:</span><span className="text-slate-800">{profile.end_date || 'Until stopped'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Next Invoice:</span><span className="text-violet-600 font-medium">{profile.next_invoice_date || '—'}</span></div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-violet-600 uppercase font-bold tracking-wider mb-3">Invoice Details</p>
                <div className="space-y-2 text-[12px]">
                  <div className="flex justify-between"><span className="text-slate-500">Amount:</span><span className="text-violet-600 font-bold text-base">{formatCurrency(profile.total_amount)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Customer:</span><span className="text-slate-800">{profile.customer_name}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Generated:</span><span className="text-slate-800">{profile.generated_count || 0} invoices</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Payment Terms:</span><span className="text-slate-800">Net {profile.payment_terms_days || 15}</span></div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
                <p className="text-[10px] text-violet-600 uppercase font-bold tracking-wider mb-3">Quick Actions</p>
                <div className="space-y-2">
                  {profile.status === 'ACTIVE' && (
                    <button onClick={() => onAction(profile.id, 'generate')} className="w-full text-left text-[12px] text-violet-600 hover:text-violet-700 font-medium py-2.5 px-3 bg-violet-50 rounded-lg hover:bg-violet-100">
                      → Generate Invoice Now
                    </button>
                  )}
                  <button onClick={() => navigate(`/business/${bid}/sales-invoices?recurring_id=${profile.id}`)} className="w-full text-left text-[12px] text-blue-600 hover:text-blue-700 font-medium py-2.5 px-3 bg-blue-50 rounded-lg hover:bg-blue-100">
                    → View Generated Invoices
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const RecurringInvoicesPage = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [profiles, setProfiles] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (bid) fetchProfiles();
  }, [bid, statusFilter]);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const response = await api.get(`/recurring-invoices?${params}`);
      const data = response.data;
      setProfiles(data.recurring_invoices || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (profileId, action) => {
    try {
      const method = action === 'generate' ? 'post' : 'patch';
      const response = await api[method](`/recurring-invoices/${profileId}/${action}`);
      const data = response.data;
      if (action === 'generate') toast.success(`Invoice ${data.invoice?.invoice_number} created!`);
      else toast.success(`Profile ${action}d successfully`);
      fetchProfiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const filteredProfiles = profiles.filter(p =>
    !searchQuery ||
    p.profile_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const upcomingThisMonth = profiles.filter(p => {
    if (p.status !== 'ACTIVE' || !p.next_invoice_date) return false;
    const nextDate = new Date(p.next_invoice_date);
    const now = new Date();
    return nextDate.getMonth() === now.getMonth() && nextDate.getFullYear() === now.getFullYear();
  });

  const expectedMonthlyRevenue = profiles.filter(p => p.status === 'ACTIVE').reduce((sum, p) => {
    const amount = p.total_amount || 0;
    const freq = p.frequency;
    if (freq === 'DAILY') return sum + (amount * 30);
    if (freq === 'WEEKLY') return sum + (amount * 4);
    if (freq === 'BIWEEKLY') return sum + (amount * 2);
    if (freq === 'MONTHLY') return sum + amount;
    if (freq === 'QUARTERLY') return sum + (amount / 3);
    if (freq === 'HALF_YEARLY') return sum + (amount / 6);
    if (freq === 'YEARLY') return sum + (amount / 12);
    return sum + amount;
  }, 0);

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  return (
    <div className="h-full flex flex-col bg-[#F7F8FA]">
      {/* Header */}
      <div className="flex-shrink-0 bg-[#F7F8FA] px-6 pt-5 pb-4">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h1 className="text-xl font-semibold text-slate-800 tracking-tight">Recurring Invoices</h1>
              <p className="text-[13px] text-slate-500 mt-0.5 font-normal">Automate your billing, never miss an invoice</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-all text-[13px] font-medium shadow-sm"
            >
              <Plus className="w-4 h-4" strokeWidth={2} />
              New Schedule
            </button>
          </div>

          {/* KPI Stats */}
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
                <Repeat className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Total Profiles</p>
                <p className="text-lg font-bold text-slate-800">{stats.total || profiles.length}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Play className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Active</p>
                <p className="text-lg font-bold text-emerald-600">{stats.active || 0}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Pause className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Paused</p>
                <p className="text-lg font-bold text-amber-600">{stats.paused || 0}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <CalendarDays className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Upcoming</p>
                <p className="text-lg font-bold text-purple-600">{upcomingThisMonth.length}</p>
              </div>
            </div>

            <div className="inline-flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-violet-600 to-violet-700 rounded-xl shadow-sm text-white">
              <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] text-violet-200 uppercase tracking-wider font-semibold">Expected Monthly</p>
                <p className="text-lg font-bold">{formatCompactCurrency(expectedMonthlyRevenue)}</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex-1 relative max-w-md">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or customer..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white rounded-lg text-[13px] focus:ring-2 focus:ring-violet-500/20 focus:outline-none placeholder:text-slate-400 shadow-sm border border-slate-200"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2.5 bg-white rounded-lg text-[13px] text-slate-700 border border-slate-200 shadow-sm focus:ring-2 focus:ring-violet-500/20"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            {(statusFilter !== 'all' || searchQuery) && (
              <button onClick={clearFilters} className="inline-flex items-center gap-1.5 px-3 py-2.5 text-slate-500 hover:text-slate-700 text-[13px] font-medium bg-white rounded-lg border border-slate-200 shadow-sm">
                <RefreshCw className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-hidden px-6 pb-5">
        <div className="max-w-[1600px] mx-auto h-full flex flex-col">
          <div className="tl-table-container flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-[3px] border-slate-200 border-t-violet-600 rounded-full animate-spin"></div>
              </div>
            ) : filteredProfiles.length === 0 ? (
              <div className="tl-empty-state flex-1 flex flex-col items-center justify-center bg-white rounded-xl border border-slate-200">
                <div className="w-16 h-16 bg-violet-100 rounded-2xl flex items-center justify-center mb-4">
                  <Repeat className="w-8 h-8 text-violet-500" />
                </div>
                <p className="text-lg font-semibold text-slate-700">{searchQuery || statusFilter !== 'all' ? 'No profiles found' : 'No recurring profiles yet'}</p>
                <p className="text-slate-500 mt-1 mb-5">{searchQuery || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first recurring invoice schedule'}</p>
                {!searchQuery && statusFilter === 'all' && (
                  <button onClick={() => setShowCreateModal(true)} className="px-5 py-2.5 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 shadow-sm">
                    Create Schedule
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="flex-shrink-0">
                  <table className="tl-table">
                    <thead>
                      <tr>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '3%' }}></th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '18%' }}>Profile</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '10%' }}>Frequency</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '12%' }}>Next Invoice</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200 tl-th-right" style={{ width: '12%' }}>Amount</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '10%' }}>Generated</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '10%' }}>Status</th>
                        <th className="tl-th bg-violet-50/70 text-violet-700 border-b border-violet-200" style={{ width: '15%' }}>Actions</th>
                      </tr>
                    </thead>
                  </table>
                </div>
                <div className="flex-1 overflow-y-auto">
                  <table className="tl-table">
                    <tbody>
                      {filteredProfiles.map((profile, index) => (
                        <ProfileRow
                          key={profile.id}
                          profile={profile}
                          index={index}
                          onAction={handleAction}
                          bid={bid}
                          navigate={navigate}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="tl-table-footer flex items-center justify-between py-3 px-4 bg-white border-t border-slate-200 rounded-b-xl">
                  <p className="text-[13px] text-slate-600">Showing <span className="font-semibold text-slate-800">{filteredProfiles.length}</span> profiles</p>
                  <p className="text-[13px] text-slate-600">Expected Monthly: <span className="font-bold text-violet-600">{formatCompactCurrency(expectedMonthlyRevenue)}</span></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateRecurringModal
          bid={bid}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); fetchProfiles(); }}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 4-STEP WIZARD MODAL
// ═══════════════════════════════════════════════════════════════════════════════

const CreateRecurringModal = ({ bid, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '', customer_name: '', customer_email: '', billing_address: '',
    items: [{ item_name: '', quantity: 1, rate: 0, tax_rate: 0, amount: 0 }],
    subtotal: 0, discount_amount: 0, tax_amount: 0, total_amount: 0,
    profile_name: '', frequency: 'MONTHLY', start_date: new Date().toISOString().split('T')[0], end_date: '', max_occurrences: '',
    auto_generate: true, auto_send_email: false, auto_send_whatsapp: false, payment_terms_days: 15, customer_notes: '', terms_and_conditions: ''
  });

  useEffect(() => { fetchCustomers(); fetchItems(); }, []);

  const fetchCustomers = async () => {
    try {
      const res = await api.get('/parties?type=customer');
      setCustomers(res.data.parties || []);
    } catch (e) { console.error(e); }
  };

  const fetchItems = async () => {
    try {
      const res = await api.get('/items');
      setItems(res.data.items || []);
    } catch (e) { console.error(e); }
  };

  const selectCustomer = (customer) => {
    setFormData(f => ({ ...f, customer_id: customer.id, customer_name: customer.name, customer_email: customer.email || '', billing_address: customer.billing_address || customer.address || '' }));
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    const qty = parseFloat(newItems[index].quantity) || 0;
    const rate = parseFloat(newItems[index].rate) || 0;
    const taxRate = parseFloat(newItems[index].tax_rate) || 0;
    const baseAmount = qty * rate;
    const taxAmount = baseAmount * (taxRate / 100);
    newItems[index].amount = baseAmount + taxAmount;
    newItems[index].tax_amount = taxAmount;
    const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0), 0);
    const totalTax = newItems.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
    const total = subtotal + totalTax - (parseFloat(formData.discount_amount) || 0);
    setFormData(f => ({ ...f, items: newItems, subtotal, tax_amount: totalTax, total_amount: total }));
  };

  const addItem = () => setFormData(f => ({ ...f, items: [...f.items, { item_name: '', quantity: 1, rate: 0, tax_rate: 0, amount: 0 }] }));
  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      const subtotal = newItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0), 0);
      const totalTax = newItems.reduce((sum, item) => sum + (parseFloat(item.tax_amount) || 0), 0);
      setFormData(f => ({ ...f, items: newItems, subtotal, tax_amount: totalTax, total_amount: subtotal + totalTax }));
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.customer_id;
    if (step === 2) return formData.items.some(i => i.item_name && i.rate > 0);
    if (step === 3) return formData.frequency && formData.start_date;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        customer_id: formData.customer_id,
        profile_name: formData.profile_name || `${formData.customer_name} - ${formData.frequency}`,
        frequency: formData.frequency,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        max_occurrences: formData.max_occurrences ? parseInt(formData.max_occurrences) : null,
        items: formData.items.map(item => ({
          item_name: item.item_name, quantity: parseFloat(item.quantity) || 1, rate: parseFloat(item.rate) || 0,
          tax_rate: parseFloat(item.tax_rate) || 0, tax_amount: parseFloat(item.tax_amount) || 0, amount: parseFloat(item.amount) || 0
        })),
        subtotal: formData.subtotal, discount_amount: formData.discount_amount, tax_amount: formData.tax_amount, total_amount: formData.total_amount,
        payment_terms_days: parseInt(formData.payment_terms_days) || 15, customer_notes: formData.customer_notes,
        terms_and_conditions: formData.terms_and_conditions, auto_send_email: formData.auto_send_email
      };
      await api.post('/recurring-invoices', payload);
      toast.success('Schedule created!');
      onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;
  const STEPS = [{ num: 1, title: 'Customer', icon: User }, { num: 2, title: 'Items', icon: Package }, { num: 3, title: 'Schedule', icon: Calendar }, { num: 4, title: 'Automation', icon: Zap }];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-800">Create Recurring Invoice</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg"><XCircle className="w-5 h-5 text-slate-400" /></button>
          </div>
          <div className="flex items-center justify-between">
            {STEPS.map((s, idx) => {
              const Icon = s.icon;
              const isActive = step === s.num;
              const isComplete = step > s.num;
              return (
                <React.Fragment key={s.num}>
                  <div className={`flex items-center gap-2 ${isActive ? 'text-violet-600' : isComplete ? 'text-emerald-600' : 'text-slate-400'}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isActive ? 'bg-violet-100 text-violet-600' : isComplete ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100'}`}>
                      {isComplete ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className="text-sm font-medium hidden sm:block">{s.title}</span>
                  </div>
                  {idx < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${step > s.num ? 'bg-emerald-400' : 'bg-slate-200'}`} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer *</label>
                <div className="relative">
                  <input type="text" value={customerSearch} onChange={(e) => { setCustomerSearch(e.target.value); setShowCustomerDropdown(true); }} onFocus={() => setShowCustomerDropdown(true)} placeholder="Search customer..." className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" />
                  {showCustomerDropdown && customerSearch && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-20">
                      {customers.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).map(c => (
                        <div key={c.id} onClick={() => selectCustomer(c)} className="px-4 py-2.5 hover:bg-violet-50 cursor-pointer text-sm">
                          <p className="font-medium text-slate-800">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.email || c.phone || 'No contact'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {formData.customer_id && (
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="font-medium text-emerald-800">{formData.customer_name}</p>
                  <p className="text-sm text-emerald-600">{formData.customer_email || 'No email'}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Billing Address (Optional)</label>
                <textarea value={formData.billing_address} onChange={(e) => setFormData(f => ({ ...f, billing_address: e.target.value }))} rows={3} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm resize-none" placeholder="Enter billing address..." />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100"><tr><th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Item</th><th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 w-20">Qty</th><th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 w-28">Rate</th><th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 w-20">Tax %</th><th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 w-28">Amount</th><th className="px-3 py-2 w-10"></th></tr></thead>
                  <tbody className="divide-y divide-slate-200">
                    {formData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-3 py-2"><input type="text" value={item.item_name} onChange={(e) => updateItem(idx, 'item_name', e.target.value)} placeholder="Item name" className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm" list={`items-${idx}`} /><datalist id={`items-${idx}`}>{items.map(i => <option key={i.id} value={i.name} />)}</datalist></td>
                        <td className="px-3 py-2"><input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm text-center" min="1" /></td>
                        <td className="px-3 py-2"><input type="number" value={item.rate} onChange={(e) => updateItem(idx, 'rate', e.target.value)} placeholder="0.00" className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm text-right" /></td>
                        <td className="px-3 py-2"><select value={item.tax_rate} onChange={(e) => updateItem(idx, 'tax_rate', e.target.value)} className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm"><option value="0">0%</option><option value="5">5%</option><option value="12">12%</option><option value="18">18%</option><option value="28">28%</option></select></td>
                        <td className="px-3 py-2 text-right font-medium text-sm">{formatCurrency(item.amount)}</td>
                        <td className="px-3 py-2"><button onClick={() => removeItem(idx)} className="p-1 text-slate-400 hover:text-red-600" disabled={formData.items.length === 1}><Trash2 className="w-4 h-4" /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-3 border-t border-slate-200"><button onClick={addItem} className="text-sm text-violet-600 hover:text-violet-700 font-medium flex items-center gap-1"><Plus className="w-4 h-4" /> Add Item</button></div>
              </div>
              <div className="flex justify-end">
                <div className="w-64 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-medium">{formatCurrency(formData.subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tax</span><span className="font-medium">{formatCurrency(formData.tax_amount)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-slate-200"><span className="font-semibold text-slate-800">Total</span><span className="font-bold text-violet-600 text-lg">{formatCurrency(formData.total_amount)}</span></div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Profile Name</label><input type="text" value={formData.profile_name} onChange={(e) => setFormData(f => ({ ...f, profile_name: e.target.value }))} placeholder={`${formData.customer_name || 'Customer'} - Monthly`} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" /></div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Frequency *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {FREQUENCIES.map(freq => (
                    <button key={freq.value} type="button" onClick={() => setFormData(f => ({ ...f, frequency: freq.value }))} className={`p-3 rounded-xl border-2 text-left transition-all ${formData.frequency === freq.value ? 'border-violet-500 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                      <p className={`text-sm font-medium ${formData.frequency === freq.value ? 'text-violet-700' : 'text-slate-700'}`}>{freq.label}</p>
                      <p className="text-xs text-slate-500">{freq.description}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label><input type="date" value={formData.start_date} onChange={(e) => setFormData(f => ({ ...f, start_date: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">End Date</label><input type="date" value={formData.end_date} onChange={(e) => setFormData(f => ({ ...f, end_date: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm" /><p className="text-xs text-slate-500 mt-1">Leave empty for "Until stopped"</p></div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="p-4 bg-violet-50 rounded-xl border border-violet-200">
                <h4 className="font-medium text-violet-800 mb-3">Automation Options</h4>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={formData.auto_generate} onChange={(e) => setFormData(f => ({ ...f, auto_generate: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-violet-600" /><div><p className="text-sm font-medium text-slate-700">Auto-generate invoice</p><p className="text-xs text-slate-500">Automatically create invoice on schedule</p></div></label>
                  <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={formData.auto_send_email} onChange={(e) => setFormData(f => ({ ...f, auto_send_email: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-violet-600" /><div><p className="text-sm font-medium text-slate-700">Auto-send via Email</p><p className="text-xs text-slate-500">Send invoice to customer automatically</p></div></label>
                  <label className="flex items-center gap-3 cursor-pointer"><input type="checkbox" checked={formData.auto_send_whatsapp} onChange={(e) => setFormData(f => ({ ...f, auto_send_whatsapp: e.target.checked }))} className="w-4 h-4 rounded border-slate-300 text-violet-600" /><div><p className="text-sm font-medium text-slate-700">Auto-send via WhatsApp</p><p className="text-xs text-slate-500">Send via WhatsApp</p></div></label>
                </div>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-2"><AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" /><div><p className="text-sm font-medium text-amber-800">Payment Status</p><p className="text-xs text-amber-700 mt-1">Generated invoices will always be marked as <strong>"Unpaid"</strong>. You must manually record payments when received.</p></div></div>
              </div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Payment Terms (Days)</label><select value={formData.payment_terms_days} onChange={(e) => setFormData(f => ({ ...f, payment_terms_days: e.target.value }))} className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm"><option value="0">Due on Receipt</option><option value="7">Net 7</option><option value="15">Net 15</option><option value="30">Net 30</option><option value="45">Net 45</option><option value="60">Net 60</option></select></div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} className="flex items-center gap-1 px-4 py-2 text-slate-600 hover:text-slate-800"><ChevronLeft className="w-4 h-4" />{step > 1 ? 'Back' : 'Cancel'}</button>
          {step < 4 ? (
            <button onClick={() => setStep(step + 1)} disabled={!canProceed()} className="flex items-center gap-1 px-5 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50">Next <ChevronRight className="w-4 h-4" /></button>
          ) : (
            <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-1 px-5 py-2 bg-violet-600 text-white rounded-lg font-medium hover:bg-violet-700 disabled:opacity-50">{saving ? 'Creating...' : 'Create Schedule'}</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecurringInvoicesPage;
